import { useLang } from 'hooks/lang';

import { Modal } from 'components/Blocks';

import { SingleComment } from './SingleComment';
import { LoadingComment } from './LoadingComment';

import { addCommentContent } from './utils';

import { BaseComment } from './types';
import { LoadParams, useLoad } from 'entities';

export type HistoryCommentTarget = Pick<
  LoadParams,
  | 'projects.taskCommentHistory'
  | 'projects.issueCommentHistory'
  | 'feedback.suggestionCommentHistory'
  | 'projects.recruitmentCommentHistory'
>;

type SplitTarget<T extends keyof HistoryCommentTarget> = T extends `${infer Mod}.${infer Action}`
  ? [Mod, Action]
  : [string, string];

interface CommentHistoryModalProps<Comment extends BaseComment> {
  children?: never;
  close: () => void;
  comment: Comment | null;

  historyTarget: keyof HistoryCommentTarget;
}

export function CommentHistoryModal<Comment extends BaseComment>({
  close,
  comment,

  historyTarget,
}: CommentHistoryModalProps<Comment>) {
  const { t } = useLang();

  const [commentModule, loadName] = historyTarget.split('.') as SplitTarget<typeof historyTarget>;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { data, loading, error } = useLoad(commentModule, loadName, {
    params: { id: comment?.id },

    blockLoad: !comment,

    defaultValue: [],
  });

  const oldComments = [comment as BaseComment, ...data!]
    .map((old) => ({
      ...old,
      user: (comment as BaseComment)?.user,
      timestamp: old?.updatedAt || old?.timestamp,
    }))
    .filter((c) => c?.text);

  const currentComment = oldComments[0] || comment;

  return (
    <Modal isOpen={!!comment} close={close} title={t('comment-history')} width="max(40vw, 290px)">
      {currentComment && (
        <SingleComment
          key={`old-${currentComment.timestamp}`}
          canEdit={false}
          comment={addCommentContent(currentComment)}
        />
      )}

      {loading || error ? (
        <>
          {Array.from({ length: 3 }).map((_, index) => (
            <LoadingComment key={`loading--comment--${index}`} />
          ))}
        </>
      ) : (
        oldComments
          .slice(1)
          .map((oldComment) => (
            <SingleComment
              key={`old-${oldComment.timestamp}`}
              canEdit={false}
              comment={{ ...addCommentContent(oldComment), files: [] }}
            />
          ))
      )}
    </Modal>
  );
}
